import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import * as analytics from '@eb/site-analytics';
import { withRouter } from 'react-router-dom';
import { Divider } from '@eb/eds-divider';
import { withMainControls } from '@eb/eds-structure';
import {
    withEventSaveNotification,
    getCanonicalSearchUrl,
    collections as collectionsDuck,
} from '@eb/destination-utils';

import {
    handleApplyFilter as handleApplyFilterAction,
    handleApplyFilterSimple as handleApplyFilterSimpleAction,
} from '../../actions/search';
import { saveEvent as saveEventAction } from '../../actions/event';
import { addSearchIdToGaAction } from '../../utils/analytics';

import {
    isNextDisabled,
    getEventSuggestions,
    getTrendingSearchData,
    hasEventResults,
    getParsedSuggestedFilters,
    getRandomizedSuggestedTags,
} from './selectors';
import {
    TagSuggestions,
    FilterSuggestions,
    EventSuggestions,
} from './components/Suggestions';
import { LowResults, NoResults } from './components/EmptyState';
import { TrendingSearchesAnchor } from './components/TrendingSearchesAnchor';
import { GA_SEARCH_REDIRECTION_CATEGORY } from './constants';

import './SearchResultsPivotsContainer.scss';

const SearchPivotSuggestions = (props) => {
    //TODO: trigger this on scroll into view as opposed to on mount
    useEffect(() => {
        props.trackGAEvent({
            action: 'EndResultsSectionShown',
            nonInteraction: true,
        });
    }, [props.isLoading]);

    return (
        <>
            <section className="search-result-pivots__empty-state">
                {props.hasEventResults ? (
                    <LowResults containerClassname="eds-l-pad-hor-6" />
                ) : (
                    <NoResults containerClassname="eds-l-pad-hor-6" />
                )}
            </section>
            <EventSuggestions {...props} />
            <TagSuggestions {...props} />
            <FilterSuggestions {...props} />
        </>
    );
};

/**
 * Suggested actions for the user to take given low, empty, or
 * end of search results.
 *
 * Always displays the trending searches footer.
 *
 * @param {*} props
 */
export const SearchResultsPivots = (props) => {
    const { isLoading, hasEventResults, isNextDisabled } = props;
    const shouldRenderSuggestions =
        !isLoading && (!hasEventResults || isNextDisabled);

    return (
        <div className="search-result-pivots">
            {shouldRenderSuggestions && <SearchPivotSuggestions {...props} />}
            <div className="eds-l-pad-top-6 eds-l-pad-bot-10">
                <Divider color="ui-200" />
                <TrendingSearchesAnchor
                    trendingSearchData={props.trendingSearchData}
                />
            </div>
        </div>
    );
};

const _mapStateToProps = (state) => ({
    isLoading: state.pagination.loading,
    isNextDisabled: isNextDisabled(state),
    hasEventResults: hasEventResults(state),
    userCollections: collectionsDuck.selectors.getFormattedCollections(state),
    featureFlags: state.app.featureFlags,
    suggestionsLoading: state.suggestions.loading,
    eventSuggestions: getEventSuggestions(state),
    suggestedFilters: getParsedSuggestedFilters(state),
    suggestedTags: getRandomizedSuggestedTags(state),
    isAuthenticated: state.auth.isAuthenticated,
    shareOptions: state.share.options,
    allEventsUrl: getCanonicalSearchUrl({ slug: state.location.slug }),
    trackGAEvent: (action) =>
        analytics.trackEventFromState(
            state,
            addSearchIdToGaAction(
                { ...action, category: GA_SEARCH_REDIRECTION_CATEGORY },
                state,
            ),
        ),
    trendingSearchData: getTrendingSearchData(
        state.location,
        state.app.domain,
        state.shouldShowTrendingSearchPageLink,
    ),
    trackGAEventOnPageUnload: (action) =>
        analytics.trackEventFromStateOnPageUnload(
            state,
            addSearchIdToGaAction(
                {
                    ...action,
                    category: GA_SEARCH_REDIRECTION_CATEGORY,
                },
                state,
            ),
        ),
});

const _mapDispatchToProps = (dispatch, ownProps) => ({
    onSaveEvent: (...args) => dispatch(saveEventAction(...args, true)),
    onAddOrRemoveEventToCollection: (...args) =>
        dispatch(
            collectionsDuck.actions.addOrRemoveEventToCollection(
                GA_SEARCH_REDIRECTION_CATEGORY,
            )(...args),
        ),
    onCreateCollection: (...args) =>
        dispatch(
            collectionsDuck.actions.createCollection(
                GA_SEARCH_REDIRECTION_CATEGORY,
            )(...args),
        ),
    handleApplyFilter: (...args) =>
        dispatch(handleApplyFilterAction(ownProps.history, ...args)),
    handleApplyFilterSimple: (...args) =>
        dispatch(handleApplyFilterSimpleAction(ownProps.history, ...args)),
});

const enhance = compose(
    withMainControls,
    withRouter,
    connect(_mapStateToProps, _mapDispatchToProps),
    withEventSaveNotification,
);

export default enhance(SearchResultsPivots);
